'use client';

/**
 * API namespace `seller` — orchestrator + top-level methods.
 * Verbatim extraction from frontend/src/lib/api-client.ts:
 *   - seller top-level methods: L5921-L6099 (before payouts sub-namespace)
 *   - seller header: L5886 (moved verbatim)
 *   - seller.payouts: imported from ./seller-payouts
 *   - seller.products: imported from ./seller-products
 *   - seller.orders: imported from ./seller-orders
 *   - seller.bundles: imported from ./seller-bundles
 *   - seller.coupons: imported from ./seller-coupons
 *   - seller.giftCards: imported from ./seller-gift-cards
 *
 * Part of SPEC-244 IA17 split (refactor/spec244-ia17-api-client).
 * Transformations: T-DEDENT-2, T-EXPORT, T-IMPORTS (see ledger).
 */

import { apiFetch } from '../core/fetch-client';

import type {
  AuthResponse, ApiError, RegisterRequest, LoginRequest, PublicUser,
  DashboardData, FeatureFlags, UpdateProfileInput, ChangePasswordInput,
  Setup2FAResult, Verify2FAResult, Login2FAResponse,
  ForgotPasswordInput, ResetPasswordInput, VerifyEmailInput,
  NetworkTree, WalletData, ConferencesData, CreateConferenceInput,
  CreateInterviewBody, InterviewCreatedResponse, InterviewListResponse,
  InviteBody, InviteResponse, JoinBody, JoinResponse,
  DownlineResult, QualificationResult, IDeposit, PayoutResult,
  ReconciliationResult,
  UserDepositStatus,
  CreditBalanceResponse, CreditHistoryResponse,
  CreditPurchaseRequest, CreditPurchaseResponse, CreditTransactionsResponse,
  SellerRegistrationRequest, SellerProfileResponse,
  FulfillmentConfigRequest, OnboardingStepRequest,
  SellerDashboardResponse, PublicStoreResponse,
  StoreBranding, StoreBrandingUpdate,
  SellerPayoutRequestBody, SellerPayoutBalanceResponse,
  ProductCreateRequest, ProductUpdateRequest,
  SellerProductResponse, ProductStatus,
  AdminProductListItem, AdminProductListResponse,
  ProductListResponse, PublicProductResponse, ProductListFilters,
  OrderCreateRequest, OrderCreateRequestUnion,
  OrderResponse, OrderListResponse, ShipOrderRequest,
  ProductBundle, ProductBundleResponse, ProductBundleListResponse,
  ProductBundleStatus, ProductBundleCreateInput, ProductBundleUpdateInput,
  Coupon, CouponResponse, CouponListResponse, CouponStatus,
  CouponPurchaseResponse, CouponPurchaseListResponse,
  CouponCreateInput, CouponUpdateInput, CouponPurchaseInput,
  GiftCard, GiftCardResponse, GiftCardListResponse, GiftCardStatus,
  GiftCardPurchaseResponse, GiftCardPurchaseListResponse,
  GiftCardCreateInput, GiftCardUpdateInput, GiftCardPurchaseInput,
  RedemptionMethod, PurchaseStatus,
  Dispute, DisputeStatus, DisputeChannel,
  Evidence, EvidenceType, DisputeMessage,
  Review, ReviewDirection, ReviewRating,
  ReviewListItem, ReviewListResponse,
  NetworkStats, WalletTransaction, WalletStats,
  RoomParticipantStat, ConferenceStats, NetworkNode,
  AnnouncementDto, CreateAnnouncementBody, UpdateAnnouncementBody,
  KycApplicationDto, Paginated,
  DepositDto, DepositStatus,
  DepositNewPayload, PayoutDto, PayoutStatus, PayoutStatusChangePayload,
  AuditListQuery, PaginatedAuditEntries, AuditChainVerificationResult,
  KycApplication, KycReviewStatus, KycDocumentType,
  KycFatfInput, GeoLocationOption, ZodType, Landing,
  AccountingNetwork, AccountingNetworkBreakdown, AccountingSummary,
  PayoutRequestBody, PushSubscribeInput, PushUnsubscribeInput,
  AcademyTrack, AcademyCourseStatus, AcademyCourseDifficulty,
  AcademyCourse, AcademyCourseWithMeta, AcademyCatalogResponse,
  AcademyModuleListItem, AcademyModuleDetail, AcademyModuleResource,
  AcademyEnrollmentSummary, AcademyMyCourse, AcademyMyCertificate,
  AcademyVerifyResult, AcademyMarkProgressResponse, AcademyAdminCourse,
  IUserCertification, EntrepreneurProfile, EntrepreneurProfileInput,
  DiscoveryResponse,
  AccountingUserBalances, AccountingLedgerEntry,
  AccountBalanceResult, TrialBalanceRow, TrialBalanceResult,
  ReconciliationReport, ReconciliationStatusResponse,
  ReconciliationHistoryResponse, AccountingPostResult,
  UnsignedTransaction, SubmitTransactionResult,
  TransactionStatusResult, PrepareTransactionBody,
  SubmitTransactionBody, AwaitingSignaturePayout,
  WelcomeMessageResponse, WelcomeMessageInput,
  SavedAddressResponse, SavedAddressInput,
  ForbiddenWordResponse, ForbiddenWordInput,
  PublicRoomInfo, PublicRoomBySlugInfo,
} from '../core/shared-types';

import type {
  AdminUserDto, AdminUserDetailDto, AdminPurgeUserResultDto,
  FeatureFlagDto, AdminConferenceDto, LandingLimits, AdminMetricsDto,
  DepositChainInfo, UserDepositDto,
  CircuitState, CircuitBreakerMetricsDto,
  CircuitBreakerResetResultDto, CircuitBreakerStateChangePayload,
  TransactionLedgerEntryDto, TransactionDto,
  DisputeResponse, DisputeListResponse, DisputeMessageResponse,
  EvidenceUploadResponse, AppealResponse, ReviewResponse,
  BundleStats, CouponStats, GiftCardStats,
  AdminDisputeListFilters, AdminDisputeListResponse,
  AdminDisputeDetailResponse, AdminResolveDisputeBody,
  AdminResolveAppealBody, AdminModerateReviewBody,
  ReconciliationStatus,
} from '../core/types';

import type {
  ConferenceRoomResponse, InterviewRoomResponse,
  ReservationAvailabilitySlot, ReservationAvailability,
  ReservationSlot, BookingInput, ReservationCreated, ReservationListItem,
  DirectMessageDTO, SendDirectMessageInput, UploadedDirectFileDTO,
} from '../core/interfaces';

import { payouts as sellerPayouts } from './seller-payouts';
import { products as sellerProducts } from './seller-products';
import { orders as sellerOrders } from './seller-orders';
import { bundles as sellerBundles } from './seller-bundles';
import { coupons as sellerCoupons } from './seller-coupons';
import { giftCards as sellerGiftCards } from './seller-gift-cards';
import { stores as sellerStores } from './seller-stores';

export const seller = {
  /**
   * POST /api/seller/register — register a new seller.
   *
   * Creates a `Seller` document with `status='pending_onboarding'` and
   * `fulfillmentOptions={ ships: false, pickup: false, shipping: null,
   * pickupConfig: null }` (the defaults intentionally violate INV-MKT-21
   * — the rule only applies when `status='active'`). The seller then
   * completes the 3-step onboarding flow (lecture + T&C + fulfillment)
   * via `completeOnboardingStep`.
   *
   * Authz: `requireAuth` + `requireMarketSellersEnabled` + KYC gate
   * (`requireKycForPayout` middleware — kycLevel ≥ 1 per INV-MKT-19).
   *
   * Spec ref: SPEC-081 Fase 2 §5.1, §4.1 (registerSeller).
   */
  async register(
    data: SellerRegistrationRequest,
    signal?: AbortSignal,
  ): Promise<SellerProfileResponse> {
    return apiFetch<SellerProfileResponse>('/seller/register', {
      method: 'POST',
      body: JSON.stringify(data),
      signal,
    });
  },
  /**
   * GET /api/seller/profile — current user's seller profile.
   *
   * Returns `SellerProfileResponse` (all fields except sensitive admin
   * notes). `storeUrl` is the public URL `/market/tienda/{storeSlug}` for
   * the seller to share with buyers.
   *
   * Authz: self only — backend verifies the seller belongs to the
   * authenticated user.
   *
   * Founder directive 2026-10-31 (substore onboarding): optional
   * `sellerId` query param to fetch a SPECIFIC sub-store's profile
   * (NOT the user's primary store). Used by the FE onboarding page
   * (/seller/onboarding?sellerId=<new>) to load the NEW sub-store the
   * user just created — pre-fix the endpoint always returned the
   * primary store (already 'active') so the onboarding hook redirected
   * away → the user could NEVER complete onboarding for the new
   * sub-store. Pass-through: when `sellerId` is provided, the BE adds
   * it to the Seller.findOne filter + returns that specific store.
   *
   * Spec ref: SPEC-081 Fase 2 §5.1, §4.1 (getProfile).
   */
  async getProfile(
    signal?: AbortSignal,
    sellerId?: string,
  ): Promise<SellerProfileResponse> {
    const url = sellerId
      ? `/seller/profile?sellerId=${encodeURIComponent(sellerId)}`
      : '/seller/profile';
    return apiFetch<SellerProfileResponse>(url, { signal });
  },
  /**
   * POST /api/seller/onboarding/:step — complete an onboarding step.
   *
   *   - step='lecture'     → `{ completed: true }` (sets `tutorialCompletedAt`)
   *   - step='terms'       → `{ accepted: true }` (sets `termsAcceptedAt`)
   *   - step='fulfillment' → `{ fulfillment: FulfillmentConfigRequest }`
   *     (sets `fulfillmentOptions` + transitions `status` to 'active' +
   *     atomically sets `User.withdrawalFeeExempt=true` per INV-MKT-08)
   *
   * The 3-step flow is RESUMABLE (Q5 in SPEC-081 Fase 2 §10) — the
   * frontend reads `Seller.tutorialCompletedAt` + `termsAcceptedAt` on
   * mount + skips to the appropriate step. The `status='active'`
   * transition happens only on the `fulfillment` step.
   *
   * Authz: `requireAuth` + `requireMarketSellersEnabled` +
   * `requireRecent2FA` (TOTP/wallet 2FA verified within last 5 min —
   * frontend opens `TwoFactorFinancialDialog` on 403 `2FA_REQUIRED`).
   *
   * Founder directive 2026-10-31 (substore onboarding): optional
   * `sellerId` query param to target a SPECIFIC sub-store (NOT the
   * user's primary store). Used by the FE onboarding page
   * (/seller/onboarding?sellerId=<new>) to apply each step to the NEW
   * sub-store the user just created — pre-fix the endpoint always
   * targeted the primary store (already 'active') so the resume logic
   * returned status='active' + the user could NEVER complete onboarding
   * for the new sub-store. Pass-through: when `sellerId` is provided,
   * the BE adds it to the Seller.findOne filter + applies the step to
   * that specific store.
   *
   * Spec ref: SPEC-081 Fase 2 §5.1, §4.1 (completeOnboardingStep).
   */
  async completeOnboardingStep(
    step: 'lecture' | 'terms' | 'fulfillment',
    data: OnboardingStepRequest,
    signal?: AbortSignal,
    sellerId?: string,
  ): Promise<SellerProfileResponse> {
    const url = sellerId
      ? `/seller/onboarding/${step}?sellerId=${encodeURIComponent(sellerId)}`
      : `/seller/onboarding/${step}`;
    return apiFetch<SellerProfileResponse>(url, {
      method: 'POST',
      body: JSON.stringify(data),
      signal,
    });
  },
  /**
   * PUT /api/seller/fulfillment — update fulfillment config (active seller).
   *
   * Allows an active seller to update their shipping + pickup config after
   * onboarding (e.g., add pickup option later, change shipping cost). The
   * `fulfillmentConfigSchema` Zod cross-field rules (INV-MKT-21) apply —
   * the backend re-validates.
   *
   * Authz: `requireAuth` + `requireMarketSellersEnabled` + seller must be
   * `status='active'` (the backend rejects with 403 if the seller is
   * still in 'pending_onboarding' — they must complete the onboarding
   * step 3 first).
   *
   * Spec ref: SPEC-081 Fase 2 §5.1, §4.1 (updateFulfillment).
   */
  async updateFulfillment(
    config: FulfillmentConfigRequest,
    signal?: AbortSignal,
    /**
     * Founder directive 2026-10-31 (store switcher dashboard bug):
     * optional sellerId to update fulfillment for a SPECIFIC sub-store
     * (NOT the user's primary store). Used by the onboarding edit-mode
     * (?step=3&sellerId=<X>) so the fulfillment update targets the
     * SELECTED sub-store. Pass-through: when sellerId is provided, the
     * BE adds it to the Seller.findOne filter.
     */
    sellerId?: string,
  ): Promise<SellerProfileResponse> {
    const url = sellerId
      ? `/seller/fulfillment?sellerId=${encodeURIComponent(sellerId)}`
      : '/seller/fulfillment';
    return apiFetch<SellerProfileResponse>(url, {
      method: 'PUT',
      body: JSON.stringify(config),
      signal,
    });
  },
  /** PUT /api/seller/store-branding — update store branding (founder 2026-10-03 — STORE-BRANDING). */
  async updateStoreBranding(
    branding: StoreBrandingUpdate,
    signal?: AbortSignal,
    /**
     * Founder directive 2026-10-31 (store switcher dashboard bug):
     * optional sellerId to update branding for a SPECIFIC sub-store
     * (NOT the user's primary store). Used by the StoreBrandingEditor
     * when the user is editing a sub-store's branding. Pass-through.
     */
    sellerId?: string,
  ): Promise<StoreBranding> {
    const url = sellerId
      ? `/seller/store-branding?sellerId=${encodeURIComponent(sellerId)}`
      : '/seller/store-branding';
    return apiFetch<StoreBranding>(url, { method: 'PUT', body: JSON.stringify(branding), signal });
  },
  /**
   * GET /api/seller/dashboard — seller metrics for the dashboard page.
   *
   * Returns `SellerDashboardResponse` (`totalSales`, `totalOrders`,
   * `rating`, `pendingOrders` — all 0 in Fase 2 since no orders exist
   * yet; `pendingOrders` is included so the Fase 4 order lifecycle can
   * populate it without a schema change). Also returns the seller's
   * `fulfillmentOptions` + `storeUrl` for the dashboard summary card.
   *
   * Authz: self only.
   *
   * Spec ref: SPEC-081 Fase 2 §5.1, §4.1 (getDashboard).
   */
  /**
   * GET /api/seller/dashboard — seller dashboard metrics (KPIs + fulfillment).
   *
   * Returns `SellerDashboardResponse` (totalSales, totalOrders, rating,
   * pendingOrders, fulfillmentOptions, storeUrl, plus Track A #4 KPIs).
   *
   * Authz: self only — backend verifies the seller belongs to the
   * authenticated user.
   *
   * Founder directive 2026-10-31 (store switcher dashboard bug):
   * optional `sellerId` query param to fetch a SPECIFIC sub-store's
   * dashboard (NOT the user's primary store). Used by the FE dashboard
   * page when the user selects a sub-store via the StoreSwitcher —
   * pre-fix the endpoint always returned the primary store's metrics
   * so the dashboard didn't reflect the switch. Pass-through: when
   * `sellerId` is provided, the BE adds it to the Seller.findOne filter
   * + returns metrics for that sub-store.
   *
   * Spec ref: SPEC-081 Fase 2 §5.1, §4.1 (getDashboard).
   */
  async getDashboard(
    signal?: AbortSignal,
    sellerId?: string,
  ): Promise<SellerDashboardResponse> {
    const url = sellerId
      ? `/seller/dashboard?sellerId=${encodeURIComponent(sellerId)}`
      : '/seller/dashboard';
    return apiFetch<SellerDashboardResponse>(url, { signal });
  },
  /**
   * GET /api/seller/store/:storeSlug — PUBLIC store info for buyers.
   *
   * Returns `PublicStoreResponse` — a privacy-filtered subset of the
   * seller profile (NO `kycDocuments`, `taxId`, `businessName`,
   * `sellerId`, `kycStatus`, `tutorialCompletedAt`, `termsAcceptedAt`,
   * `referredBy`, `strikes`, `status`). Buyers see: `storeName`,
   * `storeSlug`, `rating`, `totalOrders`, and `fulfillmentOptions` (so
   * the public store page can render shipping methods + cost + pickup
   * location for the Google Maps iframe embed).
   *
   * Authz: PUBLIC (no auth required) — this is the only seller endpoint
   * accessible without a JWT. The backend independently rejects when
   * `market_sellers_enabled` is off.
   *
   * Spec ref: SPEC-081 Fase 2 §5.1, §4.1 (getPublicStore), §6.7 (frontend
   *           store page).
   */
  async getPublicStore(
    storeSlug: string,
    signal?: AbortSignal,
  ): Promise<PublicStoreResponse> {
    return apiFetch<PublicStoreResponse>(
      `/seller/store/${encodeURIComponent(storeSlug)}`,
      { auth: false, signal },
    );
  },
  // ───────────────────────────────────────────────────────────────────────
  // PR #987 (Sprint 139 — ADR-061 Seller balance separado).
  //
  // `payouts` sub-object — typed wrappers for the seller-specific payout
  // endpoints. All endpoints live under `/api/seller/payout/*` (PR #987 —
  // `seller-payout.controller.ts`) and require `requireAuth` +
  // `requireMarketSellersEnabled` middleware (router-level). POST /request
  // ALSO requires `requireRecent2FA` (INV-SEC-06 §9.6 — financial-grade op).
  //
  // Why a separate `payouts` sub-object (NOT a flag on the existing
  // `api.payout.*` methods)?
  //   - Pre-PR-#987: the 5% withdrawal fee exemption for sellers was
  //     enforced via the volatile `withdrawalFeeExempt` flag — easily
  //     lost (Strike 2/3 edge case). The seller endpoint
  //     `POST /api/seller/payout/request` debits `wallet.sellerBalance`
  //     (the SEPARATE marketplace-sales balance — PR #987 ADR-061) and
  //     ALWAYS applies `fee='0'` per INV-MKT-08 (structural exemption).
  //   - The general `api.payout.*` methods remain for non-seller users
  //     (matrix earnings, bonuses, deposits, gift cards — 5% fee).
  //
  // §6 Anti-Invención: NO invented shapes — request + response types come
  // from `@profileia/shared` (`schemas/seller.ts` — PR #987 Task A). The
  // Zod schema `sellerPayoutRequestSchema` is the single source of truth
  // for request body validation (the backend re-uses it via `safeParse`).
  // The frontend does NOT duplicate validation here.
  //
  // Spec ref: SPEC-NEGOCIO §10.4 (Portal de Vendedores), §4.3 (5% fee —
  //           sellers exempt per INV-MKT-08), §9.3 (INV-SEC-03 idempotency),
  //           §9.4 (INV-SEC-04 atomicity), §9.6 (INV-SEC-06 2FA); ADR-061.

  payouts: sellerPayouts,
  products: sellerProducts,
  orders: sellerOrders,
  bundles: sellerBundles,
  coupons: sellerCoupons,
  giftCards: sellerGiftCards,
  // Founder directive 2026-10-28 (SUBSTORES PR 2 — FE switcher + management):
  // `stores` sub-object — typed wrappers for the 4 sub-stores management
  // endpoints (list / create / setPrimary / softDelete). All endpoints live
  // under `/api/seller/stores/*` (PR #1509 BE) + require `requireAuth` +
  // `requireMarketSellersEnabled` + `requireSellerKycApproved` (the user
  // must already have at least ONE active KYC-approved store before they
  // can create ADDITIONAL sub-stores). The original 10-store cap was
  // REMOVED in PR 2 — unlimited sub-stores per user.
  // Spec ref: Founder directive 2026-10-28 (SUBSTORES PR 2).
  stores: sellerStores,
};

